import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { FileText, Download, FolderLock, ShieldCheck, ArrowLeft, Loader2, Lock, BadgeCheck, ExternalLink, RefreshCcw, MapPin, Sparkles, Plus, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { CustodianKitVaultSection } from "../components/CustodianKitVaultSection";
import { DualPayLinks, railUrls } from "../components/payments/DualPayLinks";

/**
 * Dedicated Document Vault route (/account/documents).
 *
 * Gates
 * -----
 * * Must be logged in — otherwise redirected to /login by <ProtectedRoute>.
 * * Must be an Active buyer or seller (or admin) — otherwise bounced to the
 *   Verification Pending page.  Every buyer category (Super-Stockist,
 *   Introducer, Guarantor, Custodian, Wholesaler, Dealer, Booking Agent) and
 *   every seller passes this check.
 *
 * Data
 * ----
 * * Templates:            GET /api/documents/templates
 * * Signed agreements:    GET /api/kyc/status  (returns `.signatures[]`)
 *
 * UI
 * --
 * A single copper spinner covers both fetches so the partner sees a clean
 * "loading vault" state even if the signed-agreements call is slow.
 */
export default function AccountDocuments() {
  const { user, api } = useAuth();
  const nav = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [signed, setSigned] = useState({ signatures: [] });
  const [certs, setCerts] = useState([]);
  const [certBusy, setCertBusy] = useState(null);
  const [topUp, setTopUp] = useState(null);      // {cert, states: [], breakup, link}
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState("");

  // Gate — bounces non-active users
  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") return;
    if ((user.status || "pending_kyc") !== "active") {
      nav("/verification-pending", { replace: true });
    }
  }, [user, nav]);

  const load = useCallback(async () => {
    if (!user) return;   // <ProtectedRoute> is still resolving, spinner shown
    if (user.role !== "admin" && user.status !== "active") {
      // Redirect handled by the effect above — release the spinner.
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [t, s, c] = await Promise.all([
        api.get("/documents/templates"),
        api.get("/kyc/status").catch(() => ({ data: { signatures: [] } })),
        api.get("/certificates/mine").catch(() => ({ data: [] })),
      ]);
      setTemplates(t.data || []);
      setSigned(s.data || { signatures: [] });
      setCerts(c.data || []);
    } catch (e) {
      setError("We couldn't reach your vault. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  useEffect(() => { load(); }, [load]);

  const download = async (key, title) => {
    setDownloading(key);
    try {
      const res = await api.get(`/documents/templates/${key}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vinci-${title.replace(/[^a-z0-9]+/gi, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the document.");
    } finally {
      setDownloading(null);
    }
  };

  const downloadCert = async (cert) => {
    setCertBusy(cert.id);
    try {
      const res = await api.get(`/certificates/${cert.id}/download.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vinci-Cert-${(cert.certificate_number || cert.id).replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        toast.error("Fee still pending — please complete payment first.");
        // Refresh so the lock state is reflected immediately.
        await load();
      } else {
        toast.error("Could not download the certificate.");
      }
    } finally {
      setCertBusy(null);
    }
  };

  const refreshPayLink = async (cert) => {
    setCertBusy(cert.id);
    try {
      const { data } = await api.post(`/certificates/${cert.id}/resend-pay-link`);
      toast.success("Fresh payment links generated — PhonePe or Razorpay.");
      // Open the new link straight away
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate the pay link.");
    } finally {
      setCertBusy(null);
    }
  };

  const pollPayStatus = async (cert) => {
    setCertBusy(cert.id);
    try {
      // Poll pay-status directly so we don't race with the certs state.
      const { data } = await api.get(`/certificates/${cert.id}/pay-status`);
      await load();
      if (data?.fee_status === "paid") {
        toast.success("Payment received — certificate unlocked.");
      } else {
        toast("Not received yet. Give the payment gateway a moment and try again.");
      }
    } catch (e) {
      toast.error("Could not check payment status.");
    } finally {
      setCertBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]" data-testid="account-documents-page">
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="border-b border-white/10 sticky top-0 z-40 bg-black/70 backdrop-blur-xl">
        <div className="max-w-[1100px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Vinci" className="h-7 w-auto" />
            <span className="font-serif text-xl">Vinci Inc.</span>
          </Link>
          <button
            data-testid="documents-back-to-account"
            onClick={() => nav("/account")}
            className="inline-flex items-center gap-2 font-grotesk text-xs uppercase tracking-[0.2em] text-[#C9C9C2] hover:text-[#F5F5F0] transition-colors"
          >
            <ArrowLeft size={14} /> Account
          </button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 py-12 md:py-16">
        <span className="overline text-[#B87333]">Compliance</span>
        <div className="flex items-center gap-4 mt-4">
          <span className="grid place-items-center w-11 h-11 rounded-full bg-[#B87333] text-black">
            <FolderLock size={20} />
          </span>
          <h1 className="font-serif text-4xl md:text-5xl tracking-[-0.02em]" data-testid="documents-title">
            Document Vault
          </h1>
        </div>
        <p className="font-body text-[#8A8A82] mt-4 max-w-2xl leading-relaxed">
          Every legal template pre-filled with your business details and every
          agreement you've electronically signed with Vinci — one place,
          re-downloadable at any time.
        </p>
        {user && user.email && (
          <p className="font-body text-xs text-[#6f6f68] mt-2" data-testid="documents-signed-in-as">
            Signed in as {user.email}
          </p>
        )}

        {/* --- Loading spinner --- */}
        {loading && (
          <div data-testid="documents-loading" className="mt-16 flex flex-col items-center gap-4 text-[#B87333]">
            <Loader2 size={36} className="animate-spin" />
            <p className="font-grotesk text-[11px] uppercase tracking-[0.28em] text-[#C9C9C2]">
              Fetching your vault…
            </p>
          </div>
        )}

        {/* --- Error state --- */}
        {!loading && error && (
          <div data-testid="documents-error" className="mt-12 border border-[#C0392B]/40 bg-[#C0392B]/[0.06] p-6 rounded-sm">
            <p className="font-body text-sm text-[#E08A7E]">{error}</p>
            <button onClick={load} data-testid="documents-retry"
                    className="mt-4 rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] hover:border-white/40">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Templates */}
            <section className="mt-14" data-testid="documents-templates-section">
              <h2 className="font-serif text-2xl md:text-3xl">Ready-to-use templates</h2>
              <p className="font-body text-sm text-[#8A8A82] mt-2 mb-6 max-w-xl leading-relaxed">
                Download, review with your counsel, and execute. Pre-filled with your business details from your profile.
              </p>
              {templates.length === 0 ? (
                <p className="font-body text-sm text-[#8A8A82]" data-testid="documents-templates-empty">
                  No templates available for your account type yet.
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="documents-templates-grid">
                  {templates.map((d) => (
                    <div key={d.key} data-testid={`documents-template-${d.key}`}
                         className="group rounded-sm border border-white/10 p-6 hover:border-[#B87333]/60 transition-colors flex flex-col">
                      <div className="flex items-start gap-4">
                        <span className="grid place-items-center w-11 h-11 rounded-full bg-[#141414] border border-white/10 text-[#B87333] shrink-0">
                          <FileText size={18} />
                        </span>
                        <div>
                          <div className="font-serif text-xl leading-snug">{d.title}</div>
                          <p className="font-body text-sm text-[#8A8A82] mt-2 leading-relaxed">{d.description}</p>
                        </div>
                      </div>
                      <button
                        data-testid={`documents-download-${d.key}`}
                        onClick={() => download(d.key, d.title)}
                        disabled={downloading === d.key}
                        className="mt-5 self-start inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors disabled:opacity-50"
                      >
                        {downloading === d.key
                          ? <><Loader2 size={13} className="animate-spin" /> Preparing…</>
                          : <><Download size={13} /> Download PDF</>}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Certificate of Authorisation — pay-first gated */}
            {certs.length > 0 && (
              <section className="mt-14 pt-10 border-t border-white/10" data-testid="documents-certificates-section">
                <div className="flex items-center gap-3 mb-2">
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-[#D8B23E] text-black">
                    <BadgeCheck size={17} />
                  </span>
                  <h2 className="font-serif text-2xl md:text-3xl">Certificate of Authorisation</h2>
                </div>
                <p className="font-body text-sm text-[#8A8A82] mt-2 mb-6 max-w-xl leading-relaxed">
                  Executed under the seal of Vinci Inc. and FFPMC Florida, USA. A one-time
                  authorisation fee unlocks the download from your vault.
                </p>
                <div className="grid gap-4" data-testid="documents-certificates-grid">
                  {certs.map((c) => {
                    const paid = c.fee_status === "paid";
                    return (
                      <div key={c.id} data-testid={`certificate-card-${c.id}`}
                           className="rounded-sm border p-6 md:p-7"
                           style={{ borderColor: paid ? "rgba(47,185,153,0.4)" : "rgba(216,178,62,0.4)",
                                    backgroundColor: paid ? "rgba(47,185,153,0.03)" : "rgba(216,178,62,0.04)" }}>
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border"
                                    style={{ color: paid ? "#2FB999" : "#D8B23E",
                                             borderColor: paid ? "#2FB99966" : "#D8B23E66" }}
                                    data-testid={`certificate-status-${c.id}`}>
                                {paid
                                  ? <><BadgeCheck size={11} className="inline -mt-0.5 mr-1" /> Paid · unlocked</>
                                  : <><Lock size={11} className="inline -mt-0.5 mr-1" /> Payment required</>}
                              </span>
                              <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82]">
                                ₹{(c.fee_inr || 41680).toLocaleString("en-IN")}
                              </span>
                            </div>
                            <div className="font-serif text-xl mt-3">{c.certificate_number}</div>
                            <div className="font-body text-xs text-[#8A8A82] mt-1">
                              {c.payload?.business_name || c.user_email}
                              {c.expires_at && ` · valid until ${new Date(c.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`}
                              {" · "}{c.state_count || c.authorized_states?.length || 1} state(s)
                            </div>
                            {(c.authorized_states || []).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2" data-testid={`certificate-states-${c.id}`}>
                                {(c.authorized_states || []).map((s) => (
                                  <span key={s} className="inline-flex items-center gap-1 rounded-full border border-white/10 text-[#C9C9C2] px-2 py-0.5 text-[10px] font-grotesk">
                                    {s}
                                  </span>
                                ))}
                              </div>
                            )}
                            {paid && c.fee_paid_at && (
                              <div className="font-body text-xs text-[#2FB999] mt-1">
                                Paid on {new Date(c.fee_paid_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 shrink-0">
                            {paid ? (
                              <>
                                <button data-testid={`cert-download-${c.id}`}
                                        onClick={() => downloadCert(c)} disabled={certBusy === c.id}
                                        className="rounded-full bg-[#2FB999] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center gap-2">
                                  {certBusy === c.id
                                    ? <><Loader2 size={13} className="animate-spin" /> Preparing…</>
                                    : <><Download size={13} /> Download PDF</>}
                                </button>
                                {(c.state_count || c.authorized_states?.length || 1) < 5 && (
                                  <button data-testid={`cert-extend-${c.id}`}
                                          onClick={() => setTopUp({ cert: c, states: [], breakup: null, result: null })}
                                          className="rounded-full border border-[#D8B23E] text-[#D8B23E] px-4 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-[#D8B23E]/10 transition-colors inline-flex items-center gap-2">
                                    <Plus size={12}/> Extend Coverage
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                {(c.payment_link_url || c.pay_urls?.phonepe) ? (
                                  <DualPayLinks testId={`cert-pay-${c.id}`} urls={railUrls(c)} amountLabel={`₹${(c.fee_inr || 41680).toLocaleString("en-IN")}`} />
                                ) : (
                                  <button data-testid={`cert-generate-paylink-${c.id}`}
                                          onClick={() => refreshPayLink(c)} disabled={certBusy === c.id}
                                          className="rounded-full bg-[#D8B23E] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center gap-2">
                                    {certBusy === c.id
                                      ? <><Loader2 size={13} className="animate-spin" /> Generating…</>
                                      : <>Generate pay link · ₹{(c.fee_inr || 41680).toLocaleString("en-IN")}</>}
                                  </button>
                                )}
                                {c.payment_link_url && (
                                  <button data-testid={`cert-refresh-paylink-${c.id}`}
                                          onClick={() => refreshPayLink(c)} disabled={certBusy === c.id}
                                          className="rounded-full border border-white/20 px-4 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#C9C9C2] hover:border-[#B87333] transition-colors disabled:opacity-50 inline-flex items-center gap-2">
                                    <RefreshCcw size={12} /> Fresh link
                                  </button>
                                )}
                                <button data-testid={`cert-poll-${c.id}`}
                                        onClick={() => pollPayStatus(c)} disabled={certBusy === c.id}
                                        className="rounded-full border border-white/20 px-4 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#C9C9C2] hover:border-white/40 transition-colors disabled:opacity-50">
                                  I've paid
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        {!paid && (
                          <p className="font-body text-xs text-[#8A8A82] mt-4 leading-relaxed">
                            The PDF unlocks automatically the moment PhonePe or Razorpay confirms your payment.
                            If you've already paid but the lock hasn't lifted, click "I've paid" to refresh.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Signed agreements — certificate row filtered out (rendered above).
                Hide the section entirely if the ONLY signature is the certificate,
                since that would leave a contradictory empty state under the cert card. */}
            {signed.signatures.filter((s) => s.agreement_key !== "authorisation_certificate").length > 0 && (
            <section className="mt-14 pt-10 border-t border-white/10" data-testid="documents-signed-section">
              <div className="flex items-center gap-3 mb-2">
                <span className="grid place-items-center w-9 h-9 rounded-full bg-[#2FB999] text-black">
                  <ShieldCheck size={17} />
                </span>
                <h2 className="font-serif text-2xl md:text-3xl">Your signed agreements</h2>
              </div>
              <p className="font-body text-sm text-[#8A8A82] mt-2 mb-6 max-w-xl leading-relaxed">
                Every agreement you've electronically signed with Vinci — re-download anytime, share with your legal or finance team.
              </p>
              {(signed?.signatures || []).filter((s) => s.agreement_key !== "authorisation_certificate").length === 0 ? (
                <p className="font-body text-sm text-[#8A8A82]" data-testid="documents-signed-empty">
                  You haven't signed any agreements yet.
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="documents-signed-grid">
                  {signed.signatures.filter((s) => s.agreement_key !== "authorisation_certificate").map((s) => {
                    const isZoho = s.signature_type === "zoho";
                    const isSandbox = s.signature_type === "sandbox";
                    const backend = process.env.REACT_APP_BACKEND_URL;
                    const href = isZoho
                      ? `${backend}/api/kyc/zoho/signed/${s.reference}.pdf`
                      : isSandbox
                        ? `${backend}/api/kyc/sandbox/signed/${s.reference}.pdf`
                        : `${backend}/api/kyc/agreements/${s.agreement_key}/signed.pdf`;
                    const dateStr = s.signed_at ? new Date(s.signed_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";
                    return (
                      <a key={s.agreement_key} href={href} target="_blank" rel="noreferrer"
                         data-testid={`documents-signed-${s.agreement_key}`}
                         className="group rounded-sm border border-white/10 p-5 hover:border-[#2FB999]/60 transition-colors flex items-start gap-4">
                        <span className="grid place-items-center w-11 h-11 rounded-full bg-[#2FB999]/[0.08] border border-[#2FB999]/60 text-[#2FB999] shrink-0">
                          <ShieldCheck size={19} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-serif text-lg leading-snug truncate">{s.agreement_title || s.agreement_key}</div>
                          <p className="font-body text-xs text-[#8A8A82] mt-1">Signed {dateStr} · {isZoho ? "Zoho" : isSandbox ? "Sandbox eSign" : "In-house"}</p>
                        </div>
                        <Download size={16} className="text-[#8A8A82] group-hover:text-[#2FB999] shrink-0 mt-1" />
                      </a>
                    );
                  })}
                </div>
              )}
            </section>
            )}

            <CustodianKitVaultSection api={api} />

            <p className="font-body text-[10px] text-[#6f6f68] mt-12">
              Templates are for reference and become binding only upon execution by authorised signatories. Please seek independent legal advice.
            </p>
          </>
        )}
      </div>
      {topUp && <TopUpStatesDrawer topUp={topUp} setTopUp={setTopUp} api={api} onPaid={load} />}
    </div>
  );
}

/**
 * TopUpStatesDrawer — right-side obsidian drawer that lets a paid partner
 * pick additional Authorised States, previews the discounted top-up fee live,
 * and generates a fresh Razorpay Payment Link on submit.
 */
function TopUpStatesDrawer({ topUp, setTopUp, api, onPaid }) {
  const { cert } = topUp;
  const alreadyCovered = cert.authorized_states || (cert.payload?.territories || []);
  const [statesList, setStatesList] = useState([]);
  const [picked, setPicked] = useState([]);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const currentCount = alreadyCovered.length || 1;
  const remaining = Math.max(0, 5 - currentCount);
  const MAX = Math.min(5, currentCount + remaining);

  useEffect(() => {
    api.get("/states").then((r) => setStatesList(r.data || [])).catch(() => setStatesList([]));
  }, [api]);

  useEffect(() => {
    const target = currentCount + picked.length;
    if (target <= currentCount) { setPreview(null); return; }
    let dead = false;
    api.get(`/certificates/fee-calc?states=${target}`)
      .then((r) => { if (!dead) setPreview(r.data); })
      .catch(() => { if (!dead) setPreview(null); });
    return () => { dead = true; };
  }, [api, picked.length, currentCount]);

  const toggle = (s) => {
    if (alreadyCovered.map((x) => (x || "").toLowerCase()).includes((s || "").toLowerCase())) return;
    setPicked((prev) => {
      if (prev.includes(s)) return prev.filter((x) => x !== s);
      if (prev.length >= remaining) { toast.warning(`Only ${remaining} more state(s) can be added (max ${MAX}).`); return prev; }
      return [...prev, s];
    });
  };

  const alreadyPaid = Number(cert.fee_inr || cert.processing_fee_inr || 41680);
  const topUpINR = preview ? Math.max(0, preview.total_inr - alreadyPaid) : 0;

  const submit = async () => {
    if (picked.length === 0) { toast.error("Pick at least one new state"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/certificates/${cert.id}/add-states`, { states: picked });
      setResult(r.data);
      toast.success("Payment link ready — pay to activate the added states");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create top-up link");
    } finally { setSubmitting(false); }
  };

  const close = () => { setTopUp(null); if (result) onPaid?.(); };

  return (
    <div className="fixed inset-0 z-[80]" data-testid="cert-topup-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={close} />
      <div className="absolute top-0 right-0 h-full w-full max-w-[520px] bg-[#070707] border-l border-[#B87333]/30 overflow-y-auto">
        <div className="sticky top-0 bg-[#070707] px-6 py-4 border-b border-white/10 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B87333] font-grotesk">Extend Certificate Coverage</div>
            <div className="font-serif text-lg text-[#F5F5F0] mt-0.5">{cert.certificate_number}</div>
          </div>
          <button onClick={close} data-testid="cert-topup-close" className="text-[#8A8A82] hover:text-[#F5F5F0] p-1"><X size={18}/></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Currently covered states */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] font-grotesk mb-2">Currently covered ({currentCount})</div>
            <div className="flex flex-wrap gap-1.5">
              {alreadyCovered.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 rounded-full border border-[#2FB999]/40 text-[#2FB999] bg-[#2FB999]/[0.06] px-2.5 py-1 text-[11px] font-grotesk">
                  <BadgeCheck size={11}/>{s}
                </span>
              ))}
            </div>
          </div>

          {remaining === 0 ? (
            <div className="border border-[#D8B23E]/30 rounded-sm p-4 bg-[#D8B23E]/[0.04] text-[#D8B23E] font-body text-sm">
              Your certificate already covers the maximum of 5 states.
            </div>
          ) : !result ? (
            <>
              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] font-grotesk">Add new states · up to {remaining} more</div>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#B87333] font-grotesk" data-testid="cert-topup-count">{picked.length}/{remaining}</span>
                </div>
                <div className="max-h-56 overflow-y-auto border border-white/10 rounded-sm p-3 bg-white/[0.02]" data-testid="cert-topup-picker">
                  <div className="flex flex-wrap gap-1.5">
                    {statesList.map((s) => {
                      const already = alreadyCovered.map((x) => (x || "").toLowerCase()).includes(s.toLowerCase());
                      const active = picked.includes(s);
                      const atCap = !active && picked.length >= remaining;
                      return (
                        <button key={s} type="button"
                                onClick={() => toggle(s)}
                                disabled={already || atCap}
                                data-testid={`cert-topup-opt-${s.replace(/\s+/g, "-")}`}
                                title={already ? "Already covered" : ""}
                                className={`rounded-full px-3 py-1 text-[11px] font-grotesk border transition-colors ${
                                  already
                                    ? "border-[#2FB999]/30 text-[#2FB999]/70 line-through cursor-not-allowed"
                                    : active
                                      ? "border-[#B87333] text-[#B87333] bg-[#B87333]/10"
                                      : atCap
                                        ? "border-white/5 text-white/20 cursor-not-allowed"
                                        : "border-white/10 text-[#C9C9C2] hover:border-white/30"
                                }`}>
                          {active ? "✓ " : ""}{s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {preview && picked.length > 0 && (
                <div className="border border-[#B87333]/30 rounded-sm p-4 bg-gradient-to-br from-[#0e0a06] to-[#0a0a0a]" data-testid="cert-topup-breakup">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#B87333] font-grotesk flex items-center gap-2">
                      <Sparkles size={12}/> Top-up Calculator
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] font-grotesk">
                      ✦ {preview.discount_pct}% multi-state off
                    </span>
                  </div>
                  <div className="space-y-1.5 font-body text-[12px] text-[#C9C9C2]">
                    <div className="flex justify-between">
                      <span>Combined fee · {preview.state_count} state(s)</span>
                      <span className="tabular-nums text-[#F5F5F0]">₹{preview.total_inr.toLocaleString("en-IN")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Already paid</span>
                      <span className="tabular-nums text-[#8A8A82]">− ₹{alreadyPaid.toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10 flex items-baseline justify-between">
                    <span className="text-[10px] uppercase tracking-[0.24em] text-[#B87333] font-grotesk">You pay now</span>
                    <span className="font-serif text-2xl text-[#F5F5F0] tabular-nums" data-testid="cert-topup-payable">₹{topUpINR.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="text-[10px] text-[#8A8A82] mt-2 leading-relaxed">
                    Just <span className="text-[#D8B23E] font-semibold">₹{(topUpINR / Math.max(1, picked.length)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span> per new state — thanks to the multi-state discount.
                  </div>
                </div>
              )}

              <button data-testid="cert-topup-submit" onClick={submit} disabled={submitting || picked.length === 0}
                      className="w-full rounded-full bg-[#B87333] text-black px-6 py-3 text-[11px] uppercase tracking-[0.28em] font-grotesk flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-40">
                {submitting ? <Loader2 size={14} className="animate-spin"/> : <MapPin size={14}/>}
                Generate Pay Links · ₹{topUpINR.toLocaleString("en-IN")}
              </button>
            </>
          ) : (
            <div className="border border-[#2FB999]/40 rounded-sm p-5 bg-[#2FB999]/[0.05]" data-testid="cert-topup-result">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#2FB999] font-grotesk mb-2">Payment link ready</div>
              <div className="font-serif text-xl text-[#F5F5F0]">₹{result.top_up_inr.toLocaleString("en-IN")}</div>
              <div className="font-body text-xs text-[#8A8A82] mt-1">
                For {result.new_states.length} new state(s): {result.new_states.join(", ")}
              </div>
              <div className="mt-4" data-testid="cert-topup-paylink">
                <DualPayLinks testId="cert-topup-pay" urls={railUrls(result)} amountLabel={`₹${result.top_up_inr.toLocaleString("en-IN")}`} />
              </div>
              <p className="text-[10px] text-[#8A8A82] mt-3 leading-relaxed">
                The new states will be added to your certificate automatically the moment the gateway confirms the payment — on either rail.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

